import React from 'react';
import { Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import { parseDateTime, timeZoneAbbreviation, DATE_TIME_FORMAT } from '../../../utils/datetime';
import styles from './styles/date-time-cell.module.scss';

/**
 * Absolute date + time cell (replaces the relative "2 mins ago" rendering in
 * the Users table). Four states, each visibly different:
 *
 *  - a value       → `MM/DD/YYYY h:mm A` plus the abbreviation of the viewer's
 *                    zone AT THAT INSTANT (EST vs EDT)
 *  - `null`        → `neverLabel` ("Never") — the API states the user has no
 *                    such event; never rendered as a dash
 *  - `undefined`   → `notLoadedLabel` ("—") — the field is absent, i.e. the
 *                    payload predates the column or was not loaded
 *  - unparseable   → `notLoadedLabel` labelled "Invalid date" with the raw
 *                    value as title — never "Never", which would assert a fact
 *                    the API did not
 *
 * @param {Object} props
 * @param {string | Date | null | undefined} props.value
 * @param {string} [props.neverLabel='Never']
 * @param {string} [props.notLoadedLabel='—']
 * @param {boolean} [props.showTimeZone=true]
 * @param {string} [props.format=DATE_TIME_FORMAT] moment format; pass
 *        `DATE_TIME_SECONDS_FORMAT` where seconds matter. The ZONE is
 *        unaffected — this cell always renders in the viewer's browser zone.
 * @param {string} [props.className]
 */
function DateTimeCell({
    value,
    neverLabel = 'Never',
    notLoadedLabel = '—',
    showTimeZone = true,
    format = DATE_TIME_FORMAT,
    className,
}) {
    if (value === undefined) {
        return (
            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={className} aria-label="Not loaded">
                {notLoadedLabel}
            </Text>
        );
    }

    if (value === null) {
        return (
            <Text
                as={TextAs.SPAN}
                size={TextSizes.SMALL}
                className={`${styles.never} ${className || ''}`.trim()}
                aria-label={neverLabel}
            >
                {neverLabel}
            </Text>
        );
    }

    const m = parseDateTime(value);
    if (!m) {
        return (
            <Text
                as={TextAs.SPAN}
                size={TextSizes.SMALL}
                className={className}
                aria-label="Invalid date"
                title={String(value)}
            >
                {notLoadedLabel}
            </Text>
        );
    }

    return (
        <Text
            as={TextAs.SPAN}
            size={TextSizes.SMALL}
            className={`${styles.value} ${className || ''}`.trim()}
            title={m.toISOString()}
        >
            {m.format(format)}
            {showTimeZone ? <span className={styles.zone}>{timeZoneAbbreviation(m)}</span> : null}
        </Text>
    );
}

export default DateTimeCell;
